import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';

import { createSpacingFields } from '../fields/spacing-fields';
import { selectField } from '../fields/widgets/select-field';
import {
  CONTAINER_TEXT_COLOR_AI_SCHEMA,
  CONTAINER_TEXT_COLOR_OPTIONS,
  containerColorStyle,
  INHERIT_COLOR,
} from '../fields/container-colors';
import type { ContainerTextColor } from '../fields/container-colors';
import { BLOCK_SPACING, CONTAINER_WIDTHS, lookup, spacingToCss } from '../fields/tokens';
import type { BoxSpacing } from '../fields/tokens';
import { BackgroundField, backgroundStyle, BACKGROUND_AI_SCHEMA, DEFAULT_BACKGROUND } from '../fields';
import type { BackgroundValue } from '../fields';

import type { SlotProps } from './slot';

/** Vertical rhythm between the section's direct children. */
const CHILD_GAP = BLOCK_SPACING.sm;

/** Section's own padding default: roomier than a leaf block, so it reads as a page band. */
const DEFAULT_PADDING: BoxSpacing = { top: '48px', right: '24px', bottom: '48px', left: '24px' };

export type SectionProps = {
  /** Puck injects this slot component at render time; omitted from defaultProps. */
  content?: React.FC<SlotProps>;
  background: BackgroundValue;
  /** Pushes a text colour onto every descendant via a scoped custom property. */
  textColor: ContainerTextColor;
  /** Optional because sections saved before the grouped field have no `padding` key at all. */
  padding?: BoxSpacing;
  /** A `CONTAINER_WIDTHS` key, or a raw CSS length carried over from before this was a token. */
  maxWidth: string;
};

/**
 * Resolves the stored max width.
 *
 * Accepts a token key or a literal, mirroring `resolveTextColor`: pages authored before this was a
 * token hold raw lengths like `'800px'`, and silently re-capping them to a preset would visibly
 * reflow live content.
 */
function resolveMaxWidth(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return lookup(CONTAINER_WIDTHS, value, value);
}

/** Full-width container that groups content in a padded, optionally capped column. */
export const sectionConfig: ComponentConfig<SectionProps> = {
  label: 'Section',
  ai: {
    instructions:
      "A full-width container that groups content. IMPORTANT: Always add child components (Heading, Paragraph, Image, Button) INTO this component's content slot — never leave it empty. A Section without children is useless.",
  },
  fields: {
    content: { type: 'slot' },
    background: {
      type: 'custom',
      label: 'Background',
      render: BackgroundField,
      ai: {
        schema: BACKGROUND_AI_SCHEMA as Record<string, unknown>,
        instructions:
          "Background styling. Set color to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand colour is required. Optionally set image URL with size/position/repeat.",
      },
    },
    textColor: {
      type: 'select',
      label: 'Text color',
      options: [...CONTAINER_TEXT_COLOR_OPTIONS],
      ai: {
        schema: (CONTAINER_TEXT_COLOR_AI_SCHEMA as unknown) as Record<string, unknown>,
        instructions:
          "Text colour applied to everything inside this section. Leave as 'inherit' unless the section has a dark or coloured background, where 'inverted' keeps the copy readable.",
      },
    },
    // selectField rather than a native `select`: sections authored before this was a token hold a raw
    // length like '800px', and a native select with no matching option silently displays the first
    // one -- claiming 'Narrow' for a page rendering at 800px. The widget surfaces the stored value
    // instead.
    maxWidth: selectField(
      'Max width',
      [
        { label: 'Narrow', value: 'narrow' },
        { label: 'Normal', value: 'normal' },
        { label: 'Wide', value: 'wide' },
        { label: 'Full width', value: 'none' },
      ],
      "Caps the section container's width. 'narrow' (~768px) suits a focused column; 'normal' (~992px) is a comfortable mid-width band; 'wide' tracks the storefront's configured site width so the section aligns with the header and product grid; 'none' is full-bleed."
    ),
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: createSpacingFields(),
    },
  },
  defaultProps: {
    background: DEFAULT_BACKGROUND,
    textColor: INHERIT_COLOR,
    padding: DEFAULT_PADDING,
    maxWidth: 'none',
  },
  render: ({ content: slotContent, background, textColor, padding, maxWidth }) => {
    if (!slotContent) return <></>;
    const Slot = slotContent;

    // Sections authored before the grouped field carried `paddingX`/`paddingY` and no `padding`; they
    // adopt DEFAULT_PADDING rather than being migrated. An axis-pair fallback was tried and removed:
    // Puck merges `defaultProps` into saved props in the *editor* (core dist/index.js:10767) but not
    // in the published `Render` path, so the fallback fired on a live page yet was masked in the
    // editor by the injected default -- one section rendering two different paddings, and saving from
    // the editor would then persist the wrong one. `maxWidth` is unaffected because it kept its key,
    // so saved data supplies it and the default never masks it.
    const resolvedPadding = padding ?? DEFAULT_PADDING;

    return (
      <div
        style={{
          ...backgroundStyle(background),
          ...containerColorStyle(textColor),
          padding: spacingToCss(resolvedPadding),
          width: '100%',
        }}
      >
        <div style={{ maxWidth: resolveMaxWidth(maxWidth), margin: '0 auto' }}>
          <Slot minEmptyHeight={120} style={{ display: 'flex', flexDirection: 'column', gap: CHILD_GAP }} />
        </div>
      </div>
    );
  },
};
